/**
 * Threshold preview
 *
 * Draws the *marginal* candle for the current settings — the one that only
 * just qualifies. Parent body sits exactly at `parentBodyMinPct` of its range,
 * and the child (trailing) body exactly at `childBodyMaxPctOfParentBody` of
 * that parent body.
 *
 * Anything with a bigger parent body or a smaller child body passes; this is
 * the boundary. Numbers on their own don't tell you what "30% body" looks like
 * against a real bar, so the shape is the point.
 *
 * SVG rather than canvas: it's a static diagram that needs to stay crisp while
 * you drag a value, and it costs nothing to re-render.
 */

import React from 'react';
import { CandidateThresholds } from '../utils/setupCandidates';
import './CandidateThresholdPreview.css';

interface CandidateThresholdPreviewProps {
  thresholds: CandidateThresholds;
}

const W = 200;
const H = 116;
const PAD = 14;

const BULL = '#00e676';
const CHILD = '#00d4ff'; // matches the chart's trailing-bar colour

const CandidateThresholdPreview: React.FC<CandidateThresholdPreviewProps> = ({ thresholds }) => {
  const bodyPct = Math.min(Math.max(thresholds.parentBodyMinPct, 0), 100) / 100;
  const childPct = Math.min(Math.max(thresholds.childBodyMaxPctOfParentBody, 0), 200) / 100;

  // Parent: full height is its range; the body takes bodyPct of it, wicks split
  // the rest evenly so the drawing stays symmetric and readable.
  const range = H - PAD * 2;
  const parentBody = range * bodyPct;
  const wick = (range - parentBody) / 2;
  const top = PAD;
  const bodyTop = top + wick;
  const bodyBottom = bodyTop + parentBody;
  const bottom = top + range;

  // Child body is a share of the PARENT BODY — that's the rule. Give it small
  // wicks so it reads as a candle rather than a floating rectangle.
  const childBody = Math.max(parentBody * childPct, 1);
  const childWick = Math.max(childBody * 0.35, 4);
  const childCentre = bodyBottom - parentBody * 0.25;
  const childBodyTop = childCentre - childBody / 2;
  const childBodyBottom = childCentre + childBody / 2;

  const px = W * 0.34; // parent x
  const cx = W * 0.68; // child x
  const halfW = 13;
  const childHalfW = 8;

  return (
    <div className="ctp-wrap">
      <svg width={W} height={H} className="ctp-svg" role="img" aria-label="Threshold preview">
        {/* Parent */}
        <line x1={px} y1={top} x2={px} y2={bottom} stroke={BULL} strokeWidth={1} opacity={0.75} />
        <rect
          x={px - halfW}
          y={bodyTop}
          width={halfW * 2}
          height={Math.max(parentBody, 1)}
          fill={BULL}
          fillOpacity={0.28}
          stroke={BULL}
          strokeWidth={1.2}
        />

        {/* Child / trailing bar */}
        <line
          x1={cx}
          y1={childBodyTop - childWick}
          x2={cx}
          y2={childBodyBottom + childWick}
          stroke={CHILD}
          strokeWidth={1}
          opacity={0.75}
        />
        <rect
          x={cx - childHalfW}
          y={childBodyTop}
          width={childHalfW * 2}
          height={Math.max(childBody, 1)}
          fill={CHILD}
          fillOpacity={0.34}
          stroke={CHILD}
          strokeWidth={1.2}
        />

        {/* Parent body extent, so "% of range" is visible rather than implied */}
        <line
          x1={px - halfW - 6}
          y1={bodyTop}
          x2={px - halfW - 6}
          y2={bodyBottom}
          stroke={BULL}
          strokeWidth={1}
          opacity={0.55}
        />
        <text x={px - halfW - 9} y={(bodyTop + bodyBottom) / 2 + 3} className="ctp-label" textAnchor="end">
          {Math.round(thresholds.parentBodyMinPct)}%
        </text>

        <text x={cx + childHalfW + 4} y={childCentre + 3} className="ctp-label ctp-child">
          {Math.round(thresholds.childBodyMaxPctOfParentBody)}%
        </text>
      </svg>

      <div className="ctp-caption">
        the marginal candle: parent body {Math.round(thresholds.parentBodyMinPct)}% of its range,
        child body {Math.round(thresholds.childBodyMaxPctOfParentBody)}% of that body. bigger
        parent body or smaller child body passes.
      </div>
    </div>
  );
};

export default CandidateThresholdPreview;
